import { useEffect, useState, useRef } from "react";
import styles from "./regionDescription.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import useTranslationHook from "hooks/useTranslationHook";
import replaceAmberDomainInLinks from "@Utils/clientUtils/dangerouslySetInnerHtmlUtility/replaceAmberDomainInLinks";
import stripBlogLinks from "@Utils/clientUtils/dangerouslySetInnerHtmlUtility/stripBlogLinks";
import useNoindexSlugs from "hooks/useNoindexSlugs";
import { stripNoindexLinks } from "@Utils/noindexLinks";

type RegionDescriptionProps = {
  titleText: string;
  region: any;
  onlyShowFirstSection?: boolean;
  isDesktop?: boolean;
  className?: string;
};

const RegionDescription = ({
  titleText = "",
  region,
  onlyShowFirstSection = false,
  isDesktop = false,
  className,
}: RegionDescriptionProps) => {
  const noindexSlugs = useNoindexSlugs();
  const [showReadMore, setShowReadMore] = useState<boolean>(true);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const { t } = useTranslationHook();
  const descriptionRef = useRef<HTMLDivElement>(null);

  const toggleReadMore = () => {
    setShowReadMore((prevReadMore) => {
      const nextReadMore = !prevReadMore;
      if (nextReadMore) {
        setIsScrolling(true);
      }
      return nextReadMore;
    });
  };

  useEffect(() => {
    if (showReadMore && isScrolling && descriptionRef.current) {
      const stickyHeaderOffset = 100;
      const elementPosition = descriptionRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - stickyHeaderOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setIsScrolling(false);
    }
  }, [showReadMore, isScrolling]);
  const description = region?.description[0]?.value;
  const onKeyDown = (e: any) => {
    if (e.key === "Enter") {
      toggleReadMore();
    }
  };
  const { isWhitelabel, requestDetails, partner_uuid } = useWhitelabel();
  const [showDescription, setShowDescription] = useState<boolean>(!isWhitelabel);

  const modifyHref = (href: string) => {
    if (isWhitelabel && typeof window !== "undefined") {
      const clientHostname = window.location.hostname;
      const { hostname, port } = requestDetails;
      const protocol = !__DEV__ ? "https" : "http";
      const newDomain =
        __DEV__ && port ? `${protocol}://${hostname}:${port}` : `${protocol}://${clientHostname}`;
      const queryParams = `?partner_id=${partner_uuid}`;
      return href.replace("https://amberstudent.com", newDomain) + queryParams;
    }
    return href;
  };

  const updateLinks = (htmlContent: string) => {
    if (!htmlContent || !__CLIENT__) return htmlContent;

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const anchors = doc.querySelectorAll("a");

    anchors.forEach((anchor) => {
      const href = anchor.getAttribute("href");
      if (href) {
        const updatedHref = modifyHref(href);
        anchor.setAttribute("href", updatedHref);
      }
    });

    return doc.documentElement.outerHTML;
  };

  // HIDE_BLOG_LINKS: strip /blog* anchors from the region description content.
  const getProcessedDescription = (desc: string) =>
    stripBlogLinks(
      isWhitelabel
        ? updateLinks(stripNoindexLinks(desc, noindexSlugs))
        : replaceAmberDomainInLinks(stripNoindexLinks(desc, noindexSlugs)),
    );

  useEffect(() => {
    if (isWhitelabel) {
      setShowDescription(true);
    }
  }, []);

  const renderDesktopView = () => (
    <>
      {showReadMore ? (
        <div
          className={formatClassNames(
            styles.description,
            styles.clampedDescriptionDesktop,
            styles.deskDescSize,
          )}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: getProcessedDescription(
                `${region.description
                  .map((item: any) => (item?.name === "basic" ? item.value : ""))
                  .join(" ")
                  .replace(/<[^>]*>/g, "")
                  .slice(0, 280)}...`,
              ),
            }}
          />
          <span
            className={styles.readMoreInlineDesktop}
            onClick={toggleReadMore}
            role="button"
            onKeyDown={onKeyDown}
            tabIndex={0}
          >
            {t(
              "searchpage.components.sectionregionelements.components.regiondescription.readmorebutton",
            )}
          </span>
        </div>
      ) : (
        <>
          <div
            className={formatClassNames(
              styles.description,
              styles.fullDescriptionDesktop,
              styles.deskDescSize,
            )}
          >
            {region.description.map((item: any, index: any) => (
              <div key={index}>
                {item?.name && item?.name == "basic" && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: getProcessedDescription(item.value),
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          <div
            className={styles.readLessDesktop}
            onClick={toggleReadMore}
            role="button"
            onKeyDown={onKeyDown}
            tabIndex={0}
          >
            {t(
              "searchpage.components.sectionregionelements.components.regiondescription.readlessbutton",
            )}
          </div>
        </>
      )}
    </>
  );

  const renderMobileView = () => (
    <div className={styles.mobileDescriptionWrapper}>
      {showReadMore ? (
        <div className={formatClassNames(styles.description, styles.clampedDescriptionMobile)}>
          <span
            dangerouslySetInnerHTML={{
              __html: getProcessedDescription(
                `${region.description
                  .map((item: any) => (item?.name === "basic" ? item.value : ""))
                  .join(" ")
                  .replace(/<[^>]*>/g, "")
                  .slice(0, 240)}...`,
              ),
            }}
          />
          <span
            className={styles.readMoreInline}
            onClick={toggleReadMore}
            onKeyDown={onKeyDown}
            role="button"
            tabIndex={0}
          >
            {t(
              "searchpage.components.sectionregionelements.components.regiondescription.readmorebutton",
            )}
          </span>
        </div>
      ) : (
        <>
          <div className={styles.description}>
            {region.description.map((item: any, index: any) => (
              <div key={index}>
                {item?.name && item?.name == "basic" && (
                  <>
                    <div>
                      {item.display_name && index > 0 && (
                        <h3
                          className={formatClassNames(styles.title, index > 0 && styles.topMargin)}
                        >
                          {item.display_name}
                        </h3>
                      )}
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getProcessedDescription(item.value),
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          <div
            className={formatClassNames(styles.readMoreTextMobile, styles.topMargin)}
            onClick={toggleReadMore}
            onKeyDown={onKeyDown}
            role="button"
            tabIndex={0}
          >
            {t(
              "searchpage.components.sectionregionelements.components.regiondescription.readlessbutton",
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderFirstSectionOnly = () => (
    <div>
      {region?.description[0]?.value ? (
        <>
          {description && (
            <>
              {showReadMore ? (
                <div
                  className={formatClassNames(
                    styles.description,
                    styles.clampedDescriptionDesktop,
                    isDesktop && styles.deskDescSize,
                  )}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: getProcessedDescription(
                        `${description.replace(/<[^>]*>/g, "").slice(0, 280)}...`,
                      ),
                    }}
                  />
                  <span
                    className={styles.readMoreInlineDesktop}
                    onClick={toggleReadMore}
                    onKeyDown={onKeyDown}
                    role="button"
                    tabIndex={0}
                  >
                    {t(
                      "searchpage.components.sectionregionelements.components.regiondescription.readmorebutton",
                    )}
                  </span>
                </div>
              ) : (
                <>
                  <div
                    className={formatClassNames(
                      styles.description,
                      styles.fullDescriptionDesktop,
                      isDesktop && styles.deskDescSize,
                    )}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: getProcessedDescription(description),
                      }}
                    />
                  </div>
                  <div
                    className={styles.readLessDesktop}
                    onClick={toggleReadMore}
                    onKeyDown={onKeyDown}
                    role="button"
                    tabIndex={0}
                  >
                    {t(
                      "searchpage.components.sectionregionelements.components.regiondescription.readlessbutton",
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </>
      ) : (
        <div className={styles.emptyResult}>
          {t(
            "searchpage.components.sectionregionelements.components.regiondescription.nodescriptionfallback",
          )}
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    if (onlyShowFirstSection) return renderFirstSectionOnly();
    if (isDesktop) return renderDesktopView();
    return renderMobileView();
  };

  if (!showDescription) return null;
  if (!region?.description || region.description.length === 0) return null;

  return (
    <>
      <div
        ref={descriptionRef}
        className={formatClassNames(
          isDesktop ? styles.desktopDescriptionContainer : styles.descriptionContainer,
          isDesktop && onlyShowFirstSection && styles.desktopPadding,
          className,
        )}
      >
        {region?.name ? (
          <h2 className={formatClassNames(styles.title)}>
            {titleText} {region?.name}
          </h2>
        ) : (
          <h2 className={styles.title}>
            {t(
              "searchpage.components.sectionregionelements.components.regiondescription.fallbacktitle",
            )}
          </h2>
        )}

        {renderContent()}
      </div>
      {!isDesktop && <div className={styles.bottomBorder} />}
    </>
  );
};

export default wrapperHOC(RegionDescription, {
  componentName: "RegionDescription-SectionRegionElements-SearchPage",
  showForChina: true,
});
